/**
 * SubscribeLabelButton
 * =============================================================================
 * Subscribe / unsubscribe button for a single drug label (any region).
 *
 * - On mount, checks whether the current user is already subscribed
 *   (via GET /subscriptions/check?type=label&drug_id=<id>).
 * - Toggle subscribe (POST /subscriptions) / unsubscribe (DELETE /subscriptions/{id}).
 * - Renders nothing until /check resolves, and when it reports available=false.
 * - On a /check error the normal button is shown (the server still enforces the gate).
 * - Hidden when user is not authenticated.
 * - Handles 400 by showing the server's detail.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';

interface SubscribeLabelButtonProps {
  drugId: number;
  drugRegion?: string | null | undefined;
  drugName?: string | undefined;
}

const SubscribeLabelButton: React.FC<SubscribeLabelButtonProps> = ({
  drugId,
  drugName,
}) => {
  const { user } = useAuth();

  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [available, setAvailable] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Bumped on every label change; async completions started under an older view are dropped
  // (a drug-id compare alone would admit them after A -> B -> A navigation).
  const viewGen = useRef(0);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Check subscription status on mount / when drugId changes
  useEffect(() => {
    if (!user || !drugId) return;

    let cancelled = false;
    setChecking(true);
    setResolved(false);
    setAvailable(true);
    // Per-label state must not leak from the previous drug.
    setSubscribed(false);
    setSubscriptionId(null);
    setLoading(false);
    viewGen.current += 1;

    authApi.checkSubscription({ type: 'label', drug_id: drugId })
      .then((res) => {
        if (cancelled) return;
        setAvailable(res.available !== false);
        setSubscribed(res.subscribed);
        setSubscriptionId(res.subscription_id ?? null);
      })
      .catch(() => {
        // Check failed — show the normal button; the server still enforces the gate.
      })
      .finally(() => {
        if (!cancelled) {
          setChecking(false);
          setResolved(true);
        }
      });

    return () => { cancelled = true; };
  }, [user, drugId]);

  const handleToggle = useCallback(async () => {
    if (!user || loading) return;
    setLoading(true);
    const startedIn = viewGen.current;
    const isStale = () => viewGen.current !== startedIn;
    try {
      if (subscribed && subscriptionId != null) {
        await authApi.deleteSubscription(subscriptionId);
        if (isStale()) return;
        setSubscribed(false);
        setSubscriptionId(null);
        showToast('Unsubscribed from label updates.', 'success');
      } else if (subscribed && subscriptionId == null) {
        // Pathological: check returned subscribed=true but no ID — re-sync silently.
        setChecking(true);
        authApi.checkSubscription({ type: 'label', drug_id: drugId })
          .then((res) => {
            setSubscribed(res.subscribed);
            setSubscriptionId(res.subscription_id ?? null);
          })
          .catch(() => {})
          .finally(() => setChecking(false));
        showToast('Could not unsubscribe — refreshing status.', 'error');
      } else {
        const result = await authApi.createSubscription({
          type: 'label',
          drug_id: drugId,
          ...(drugName != null ? { name: drugName } : {}),
        });
        if (isStale()) return;
        setSubscribed(true);
        setSubscriptionId(result.id);
        showToast("Watching. You'll be notified when this label changes.", 'success');
      }
    } catch (err: unknown) {
      if (isStale()) return;
      const resp = (err as { response?: { status?: number; data?: { detail?: unknown } } }).response;
      const status = resp?.status;
      if (status === 400) {
        const detail = resp?.data?.detail;
        showToast(
          typeof detail === 'string' && detail ? detail : "Alerts aren't available for this label.",
          'error',
        );
      } else if (status === 409) {
        showToast('Already subscribed to this label.', 'error');
      } else {
        showToast('Something went wrong. Please try again.', 'error');
      }
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [user, loading, subscribed, subscriptionId, drugId, drugName, showToast]);

  if (!user) return null;

  if (!resolved || !available) return null;

  return (
    <div className="relative inline-flex items-center">
      <button
        onClick={handleToggle}
        disabled={loading || checking}
        title={subscribed ? 'Stop watching this label' : 'Get notified when this label changes. Changes are detected when this source is refreshed.'}
        className="btn-secondary inline-flex items-center gap-1.5"
      >
        {loading || checking ? (
          <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg
            className="w-3.5 h-3.5"
            fill={subscribed ? 'currentColor' : 'none'}
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        )}
        {subscribed ? 'Watching' : 'Watch for changes'}
      </button>

      {/* Toast notification */}
      {toast && (
        <div
          className={`absolute top-full left-0 mt-2 z-50 px-3 py-2 text-xs rounded-lg shadow-lg whitespace-nowrap ${
            toast.type === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SubscribeLabelButton;
